import { ArrowRightIcon, Button, ClassesIllustration, Table, TableColumns } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '../../../hooks/useIsDesktop';

const AbsenceEvaluationClassMembersTableFragment = graphql(`
  fragment AbsenceEvaluationClassMembersTableFields on Query {
    absenceSummaryQuery(from: $from, to: $to, classUuid: $classUuid) {
      __typename
      profileUuid
      daysSum
      hoursSum
      fullyPresent
      percentage
      partialPresent
      fullyAbsent
      daysExcused
    }
    classes(where: $classesWhere) {
      __typename
      uuid
      membersConnection {
        __typename
        edges {
          __typename
          node {
            __typename
            uuid
            selectName
          }
        }
      }
    }
  }
`);

type AbsenceEvaluationClassMembersTableProps = {
  onNavigate: (memberUuid: string) => void;
  query: FragmentType<typeof AbsenceEvaluationClassMembersTableFragment> | undefined;
};

type AbsenceEvaluationClassMembersTableType = {
  uuid: string;
  name: string;
  daysSum: number;
  fullyPresent: number;
  percentage: number;
  partialPresent: number;
  fullyAbsent: number;
  daysExcused: number;
};

export const AbsenceEvaluationClassMembersTable = ({ onNavigate, query }: AbsenceEvaluationClassMembersTableProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(AbsenceEvaluationClassMembersTableFragment, query);

  const members = fragment?.classes[0].membersConnection.edges ?? [];

  const isDesktop = useIsDesktop();

  const columns: TableColumns<AbsenceEvaluationClassMembersTableType>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      canExpand: true,
      header: t('students.title', { count: 1 }),
    },
    {
      id: 'daysSum',
      accessorKey: 'daysSum',
      header: t('absences.evaluation.daysSum'),
      type: 'number',
    },
    {
      id: 'fullyPresent',
      accessorKey: 'fullyPresent',
      header: t('absences.evaluation.fullyPresent'),
      type: 'number',
    },
    {
      id: 'percent',
      accessorKey: 'percent',
      header: t('common.percentage'),
      accessorFn: ({ percentage }) => `${percentage}%`,
      size: 80,
    },
    {
      id: 'partialPresent',
      accessorKey: 'partialPresent',
      header: t('absences.evaluation.partialPresent'),
      type: 'number',
    },
    {
      id: 'fullyAbsent',
      accessorKey: 'fullyAbsent',
      header: t('absences.evaluation.fullyAbsent'),
      type: 'number',
    },
    {
      id: 'daysExcused',
      accessorKey: 'daysExcused',
      header: t('absences.evaluation.daysExcused'),
      type: 'number',
    },
  ];

  const data: AbsenceEvaluationClassMembersTableType[] = members.map(({ node: m }) => {
    const memberSummary = fragment?.absenceSummaryQuery.find((s) => s.profileUuid === m.uuid);
    return {
      uuid: m.uuid,
      name: m.selectName ?? '',
      daysSum: memberSummary?.daysSum ?? 0,
      fullyPresent: memberSummary?.fullyPresent ?? 0,
      percentage: memberSummary?.percentage ?? 100,
      partialPresent: memberSummary?.partialPresent ?? 0,
      fullyAbsent: memberSummary?.fullyAbsent ?? 0,
      daysExcused: memberSummary?.daysExcused ?? 0,
    };
  });

  return (
    <Table<AbsenceEvaluationClassMembersTableType>
      data={data}
      isDesktop={isDesktop}
      columns={columns}
      emptyStateSettings={{ icon: <ClassesIllustration />, title: t('classes.noProfiles'), subtitle: '' }}
      showShadow
      showBorderRadius
      showRowHover
      onRowClick={(_event, row) => onNavigate(row.original.uuid)}
      lastCol={() => <Button hierarchy='ghost' icon={<ArrowRightIcon />} />}
    />
  );
};
